import { renderValue } from '@thinkalpha/language-services';
import type { ConcreteDataType } from '@thinkalpha/language-services';
import {
    getAllowedArgumentValue,
    isAllowedArgumentAvailableForTarget,
    isEnumeratedOptionAllowedArgument,
} from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { AllowedArgument, IndicatorParameter } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { FormulaTargetViewModel } from '@thinkalpha/ui-types/contracts/dictionary-view-model';
import z from 'zod';

export const makeOptionsSchema = (options: ParamOptions[]) => {
    const stringValues = options.map((opt) => opt.input);
    const schema = z.string().refine((value) => stringValues.includes(value), {
        message: `Value must be one of: ${stringValues.join(', ')}`,
    });
    return schema;
};

interface ParamOptions {
    input: string;
    value: AllowedArgument;
    description?: string;
}

const renderOption = (param: AllowedArgument): ParamOptions => {
    const v = getAllowedArgumentValue(param);
    let input: string;
    if (typeof v === 'string') input = v;
    else if (typeof v === 'boolean') input = `${v}`;
    else if (typeof v === 'number') input = v.toString();
    else input = renderValue(v);

    const description = isEnumeratedOptionAllowedArgument(param) ? param.description : undefined;

    return { input, value: param, description };
};

export class IndicatorParamModel {
    #param: IndicatorParameter;

    #allOptions: ParamOptions[];

    #optionsByTarget = new Map<FormulaTargetViewModel | 'all', ParamOptions[]>();

    #schemaByTarget = new Map<FormulaTargetViewModel | 'all', z.ZodSchema<any>>();

    constructor(param: IndicatorParameter) {
        this.#param = param;
        this.#allOptions = (param.options?.values || []).map(renderOption);
        this.#optionsByTarget.set('all', this.#allOptions);
    }

    get name(): string | undefined {
        return this.#param.name;
    }

    get optional(): boolean {
        return this.#param.optional;
    }

    get type(): ConcreteDataType {
        return this.#param.dataType;
    }

    optionsForTarget(target?: FormulaTargetViewModel): ParamOptions[] {
        const key = target ?? 'all';
        let cached = this.#optionsByTarget.get(key);
        if (cached) return cached;

        cached =
            target !== undefined
                ? (this.#param.options?.values ?? [])
                      .filter((arg) => isAllowedArgumentAvailableForTarget(arg, target))
                      .map(renderOption)
                : this.#allOptions;
        this.#optionsByTarget.set(key, cached);
        return cached;
    }

    schemaForTarget(target?: FormulaTargetViewModel): z.ZodSchema<any> {
        const key = target ?? 'all';
        let cached = this.#schemaByTarget.get(key);
        if (cached) return cached;

        const options = this.optionsForTarget(target);
        cached = options.length > 0 ? makeOptionsSchema(options) : z.string();
        this.#schemaByTarget.set(key, cached);
        return cached;
    }

    /** @deprecated Use {@link optionsForTarget} so target-only values are filtered. */
    get options(): ParamOptions[] {
        return this.#allOptions;
    }

    /** @deprecated Use {@link schemaForTarget} so target-only values are rejected. */
    get schema(): z.ZodSchema<any> {
        return this.schemaForTarget(undefined);
    }

    get defaultValue() {
        return this.#param.defaultValue;
    }
}
